import {
    ECONode,
    Organization,
    OrgHat,
    OrgPart,
    OrgSecurityGroup,
    SecurityTask,
    SecurityTaskOperations,
    BulkPartNode,
    BulkGraftPoint,
    InviteUserDialogResult,
    CreateInvitationsResponse,
    CheckEmailStatusResponse,
    ListInvitationsResponse,
    ResendInvitationResponse,
    RevokeInvitationResponse,
    DeleteInvitationResponse,
    InvitationDocument,
    InvitationStatus,
} from '@newgenus/common';
import {
    debounceTime,
    filter,
    first,
    from,
    Observable,
    of,
    Subject,
    switchMap,
    takeUntil,
    timeout,
    catchError,
    map,
} from 'rxjs';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { Timestamp } from 'firebase/firestore';
import firebase from 'firebase/compat/app';
import { ProfileColorService } from './profile-color.service';
import { stampLastChangedBy } from '../shared/last-changed-by';

@Injectable({
    providedIn: 'root',
})
export class OrgBoardService implements OnDestroy {
    /**
     * Subscribed to from the Organization Board Component.
     * Emitting on this subject will update the linking lines on the organization board.
     */
    public updateNodeLinking = new Subject<void>();

    /**
     * Emits when a colour change is made to a node.
     */
    public onColourChange = new Subject<any>();

    /**
     * Emits when a per-part SETTING that affects the tree shape/relationships
     * (e.g. `excludeFromHfa`) is toggled from a sidebar/dialog. The board applies
     * the change to its in-memory organization and rebuilds so the effect (e.g.
     * HFA cascade) recomputes immediately, while persistence happens via
     * `updatePart`. Payload: the partKey + the changed fields.
     */
    public partSettingChanged = new Subject<{ partKey: string; changes: Partial<OrgPart> }>();

    /**
     * Asks the board to go to a post (scroll to its card + open its sidebar), the same way the
     * held-from-above chip navigates. Emitted by the post settings card / dialog "Go to origin"
     * on a linked post. Payload: the target partKey.
     */
    public navigateToPart = new Subject<string>();

    /**
     * VITAL to keep this in sync with the Organization Board Component.
     */
    public organization!: Organization;
    public organization$ = new Subject<Organization>();

    /**
     * The previous node updated with colours.
     * Used to prevent asking the user multiple times if they want to update all children.
     * @memberof OrgBoardService
     */
    private previousColorUpdateNode: ECONode<any> | undefined;

    /**
     * Used to determine if all children should be updated with the new colours.
     * Extracted to it's own variable to prevent asking the user multiple times if they want to update all children.
     */
    private updateAllChildren = false;

    private destroy$ = new Subject<void>();

    /**
     * The API base URL for backend function calls.
     * Should be set by the host application using environment configuration.
     * Example: 'https://us-central1-newgenus-dev-19da7.cloudfunctions.net/api-'
     */
    public apiBaseUrl: string | undefined;

    private toastr = inject(ToastrService);
    private ngZone = inject(NgZone);
    private profileColorService = inject(ProfileColorService);

    constructor(
        private http: HttpClient,
        private db: AngularFirestore,
        private auth: AngularFireAuth
    ) {
        this.subscribeToColourChanges();
    }

    public inviteUsersToOrg(organizationId: string, dialogResult: InviteUserDialogResult): void {
        if (!dialogResult?.invitations?.length) return;

        // Use the invitation service to create the invitations via the backend API.
        // The invitation service handles auth token, URL construction, etc.
        from(this.auth.idToken)
            .pipe(
                filter((token) => !!token),
                first(),
                map((token) => {
                    const body = {
                        orgKey: organizationId,
                        invitations: dialogResult.invitations,
                    };
                    const headers = {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    };

                    // Build URL: the invitation create function is named 'invitationsCreate-default'
                    const url = this.apiBaseUrl
                        ? `${this.apiBaseUrl}invitationsCreate-default`
                        : `/api/invitationsCreate-default`;

                    return { body, headers, url };
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(({ body, headers, url }) => {
                this.http
                    .post<CreateInvitationsResponse>(url, body, { headers })
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: (response) => {
                            const msg = `Invitations sent: ${response.created?.length || 0} created, ${response.failed?.length || 0} failed.`;
                            this.toastr.success(msg, 'Invitations');
                            console.log('OrgBoardService > inviteUsersToOrg > response:', response);
                        },
                        error: (err) => {
                            console.error('OrgBoardService > inviteUsersToOrg > error:', err);
                            this.toastr.error('Failed to send invitations. Please try again.', 'Error');
                        },
                    });
            });
    }

    /**
     * Check email status for the invite user dialog.
     * Returns an Observable that checks Firebase Auth and org membership via the backend.
     */
    public checkEmailStatus(email: string, orgKey: string): Observable<CheckEmailStatusResponse> {
        return from(this.auth.idToken).pipe(
            filter((token) => !!token),
            first(),
            switchMap((token) => {
                const body = { email, orgKey };
                const headers = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                };

                const url = this.apiBaseUrl
                    ? `${this.apiBaseUrl}invitationsCheckEmail-default`
                    : `/api/invitationsCheckEmail-default`;

                return this.http.post<CheckEmailStatusResponse>(url, body, { headers });
            }),
            catchError((err) => {
                console.error('OrgBoardService > checkEmailStatus > error:', err);
                return of({ existsInAuth: false, existsInOrg: false } as CheckEmailStatusResponse);
            })
        );
    }

    /**
     * List all invitations for an organization, optionally filtered by status.
     * Uses a one-shot Firestore get() to always fetch fresh server data,
     * avoiding stale cache issues with valueChanges() + first().
     */
    public getOrgInvitations(orgKey: string, status?: InvitationStatus): Observable<ListInvitationsResponse> {
        const collectionRef = this.db.collection<InvitationDocument>('invitations').ref;
        let q: firebase.firestore.Query = collectionRef.where('orgKey', '==', orgKey).orderBy('sentOn', 'desc');
        if (status) {
            q = q.where('status', '==', status);
        }

        return from(q.get()).pipe(
            map((snapshot) => {
                const invitations = snapshot.docs.map((doc) => ({
                    ...(doc.data() as InvitationDocument),
                    key: doc.id,
                }));
                return { invitations, total: invitations.length } as ListInvitationsResponse;
            }),
            catchError((err) => {
                console.error('OrgBoardService > getOrgInvitations > error:', err);
                return of({ invitations: [], total: 0 } as ListInvitationsResponse);
            })
        );
    }

    /**
     * Resend a pending invitation.
     */
    public resendInvitation(invitationKey: string): Observable<ResendInvitationResponse> {
        return from(this.auth.idToken).pipe(
            filter((token) => !!token),
            first(),
            switchMap((token) => {
                const body = { invitationKey };
                const headers = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                };

                const url = this.apiBaseUrl
                    ? `${this.apiBaseUrl}invitationsResend-default`
                    : `/api/invitationsResend-default`;

                return this.http.post<ResendInvitationResponse>(url, body, { headers });
            }),
            catchError((err) => {
                console.error('OrgBoardService > resendInvitation > error:', err);
                return of({ success: false, message: 'Failed to resend invitation.' } as ResendInvitationResponse);
            })
        );
    }

    /**
     * Revoke (cancel) a pending invitation.
     */
    public revokeInvitation(invitationKey: string): Observable<RevokeInvitationResponse> {
        return from(this.auth.idToken).pipe(
            filter((token) => !!token),
            first(),
            switchMap((token) => {
                const body = { invitationKey };
                const headers = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                };

                const url = this.apiBaseUrl
                    ? `${this.apiBaseUrl}invitationsRevoke-default`
                    : `/api/invitationsRevoke-default`;

                return this.http.post<RevokeInvitationResponse>(url, body, { headers });
            }),
            catchError((err) => {
                console.error('OrgBoardService > revokeInvitation > error:', err);
                return of({ success: false, message: 'Failed to revoke invitation.' } as RevokeInvitationResponse);
            })
        );
    }

    /**
     * Permanently delete an invitation record.
     */
    public deleteInvitation(invitationKey: string): Observable<DeleteInvitationResponse> {
        return from(this.auth.idToken).pipe(
            filter((token) => !!token),
            first(),
            switchMap((token) => {
                const body = { invitationKey };
                const headers = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                };

                const url = this.apiBaseUrl
                    ? `${this.apiBaseUrl}invitationsDelete-default`
                    : `/api/invitationsDelete-default`;

                return this.http.post<DeleteInvitationResponse>(url, body, { headers });
            }),
            catchError((err) => {
                console.error('OrgBoardService > deleteInvitation > error:', err);
                return of({ success: false, message: 'Failed to delete invitation.' } as DeleteInvitationResponse);
            })
        );
    }

    private subscribeToColourChanges(): void {
        this.onColourChange.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe((node) => {
            // console.log('onColourChange > node:', node);
            const originalBc = node.bc + '';
            const originalC = node.c + '';
            const originalLinkColor = node.linkColor + '';

            // If all the original colours are the same, return.
            if (
                originalBc === node.data.colour.background &&
                originalC === node.data.colour.color &&
                originalLinkColor === node.data.colour.linkColor
            )
                return;

            node.bc = node.data.colour.background;
            node.c = node.data.colour.color;
            node.linkColor = node.data.colour.linkColor;

            // Ask only once if the user wants to update all children.
            if (!this.previousColorUpdateNode || this.previousColorUpdateNode.id !== node.id) {
                this.updateAllChildren = confirm(
                    'Would you like to update all positions beneath this position as well?'
                );
            }
            this.previousColorUpdateNode = node;

            if (this.updateAllChildren) {
                this.recursivelyUpdateChildren(node);
            } else {
                node.nodeChildren.forEach((child: any) => {
                    if (child.bc === originalBc) child.bc = node.bc;
                    if (child.c === originalC) child.c = node.c;
                    if (child.linkColor === originalLinkColor) child.linkColor = node.linkColor;
                });
            }

            if (node.data) {
                this.updateOrgPart(node.data.partKey, { colour: node.data.colour });
            }

            this.updateNodeLinking.next();
        });
    }

    private updateOrgPart(partKey: string, updateBody: Record<string, any>): void {
        const orgKey = this.organization?.key;
        if (!orgKey) return;

        this.db
            .collection('organizations')
            .doc(orgKey)
            .collection('parts')
            .doc(partKey)
            .update(updateBody)
            .then(() => {
                this.ngZone.run(() => this.toastr.success('Part updated successfully.', 'Success'));
            })
            .catch((error) => {
                console.error('updateOrgPart > error:', error);
                this.ngZone.run(() => this.toastr.error('Failed to update part.', 'Error'));
            });
    }

    /**
     * Used to monitor the changes and activities on the org board as changes are made.
     */
    public monitorSecurityTasks(): Observable<SecurityTask[]> {
        if (!this.organization) {
            console.warn('OrgBoardService > monitorSecurityTasks > No organization found.');
            return of([]);
        }

        return this.db
            .collection<any>('securityTasks', (ref) =>
                ref.where('orgId', '==', this.organization.key).orderBy('createdTimeStamp', 'desc')
            )
            .valueChanges();
    }

    /**
     * Wait for a security task to complete based on the part key and task type.
     * This method monitors the securityTasks collection for a matching task that becomes 'completed'.
     *
     * @param partKey The part key to watch for
     * @param taskType The type of task to watch for (e.g., 'addPart', 'updatePart', 'removePart')
     * @param timeoutMs Maximum time to wait for completion (default: 30 seconds)
     * @returns Promise that resolves when the task completes or rejects on timeout/error
     */
    public waitForTaskCompletion(
        partKey: string,
        taskType: SecurityTaskOperations,
        timeoutMs = 30000
    ): Promise<{ success: boolean; task?: SecurityTask; error?: string }> {
        if (!this.organization) {
            return Promise.resolve({ success: false, error: 'No organization found' });
        }

        const orgKey = this.organization.key;
        const startTime = Date.now();
        console.log(
            `[waitForTaskCompletion] Starting to watch for task: orgId=${orgKey}, partKey=${partKey}, taskType=${taskType}, startTime=${startTime}`
        );

        return new Promise((resolve) => {
            const subscription = this.db
                .collection<SecurityTask>('securityTasks', (ref) =>
                    ref
                        .where('orgId', '==', orgKey)
                        .where('partKey', '==', partKey)
                        .where('typeOfTask', '==', taskType)
                        .orderBy('createdTimeStamp', 'desc')
                        .limit(1)
                )
                .valueChanges()
                .pipe(
                    filter((tasks) => {
                        console.log(`[waitForTaskCompletion] Received ${tasks.length} tasks`);
                        if (tasks.length > 0) {
                            console.log(`[waitForTaskCompletion] First task status: ${tasks[0].status}`);
                        }
                        return tasks.length > 0;
                    }),
                    map((tasks) => tasks[0]),
                    filter((task) => {
                        // Check if task was created recently (within 5 seconds before we started watching)
                        // This prevents picking up old completed tasks
                        const taskCreatedMs =
                            task.createdTimeStamp?.toMillis?.() || (task.createdTimeStamp as any)?.seconds * 1000 || 0;
                        const isRecentTask = taskCreatedMs > startTime - 5000;

                        const isComplete =
                            task.status === 'completed' ||
                            task.status === 'fulfilled' ||
                            task.status === 'Error' ||
                            task.status === 'error' ||
                            task.status === 'failed';

                        console.log(
                            `[waitForTaskCompletion] Task status=${task.status}, isComplete=${isComplete}, taskCreatedMs=${taskCreatedMs}, isRecentTask=${isRecentTask}`
                        );

                        // Only consider recent tasks that are complete, OR tasks that are still processing
                        // (we want to wait for processing tasks to complete)
                        if (!isRecentTask && isComplete) {
                            console.log(`[waitForTaskCompletion] Ignoring old completed task, waiting for new task...`);
                            return false;
                        }

                        return isRecentTask && isComplete;
                    }),
                    first(),
                    timeout(timeoutMs),
                    catchError((err) => {
                        console.warn('[waitForTaskCompletion] Timed out or errored:', err);
                        return of(null);
                    })
                )
                .subscribe((task) => {
                    subscription.unsubscribe();

                    if (!task) {
                        console.log('[waitForTaskCompletion] No task found or timeout - assuming success');
                        resolve({ success: true }); // Timeout is ok - task might have completed before we started watching
                        return;
                    }

                    const isSuccess = task.status === 'completed' || task.status === 'fulfilled';
                    console.log(
                        `[waitForTaskCompletion] Task completed with status=${task.status}, success=${isSuccess}`
                    );
                    resolve({
                        success: isSuccess,
                        task,
                        error: isSuccess ? undefined : task.error || 'Task failed',
                    });
                });
        });
    }

    /**
     * Wait for any security task related to a specific part to complete (regardless of task type).
     * Useful for operations like delete where we want to wait for the removePart task.
     *
     * @param partKey The part key to watch for
     * @param timeoutMs Maximum time to wait for completion (default: 30 seconds)
     * @returns Promise that resolves when the task completes or rejects on timeout/error
     */
    public waitForAnyTaskOnPart(
        partKey: string,
        timeoutMs = 30000
    ): Promise<{ success: boolean; task?: SecurityTask; error?: string }> {
        if (!this.organization) {
            return Promise.resolve({ success: false, error: 'No organization found' });
        }

        const orgKey = this.organization.key;
        const startTime = Date.now();

        return new Promise((resolve) => {
            const subscription = this.db
                .collection<SecurityTask>('securityTasks', (ref) =>
                    ref
                        .where('orgId', '==', orgKey)
                        .where('partKey', '==', partKey)
                        .orderBy('createdTimeStamp', 'desc')
                        .limit(1)
                )
                .valueChanges()
                .pipe(
                    filter((tasks) => tasks.length > 0),
                    map((tasks) => tasks[0]),
                    filter((task) => {
                        // Only consider tasks created after we started watching (or within a small window)
                        const taskCreated =
                            task.createdTimeStamp?.toMillis?.() || task.createdTimeStamp?.seconds * 1000 || 0;
                        const withinWindow = taskCreated > startTime - 5000; // 5 second grace period
                        return (
                            withinWindow &&
                            (task.status === 'completed' ||
                                task.status === 'fulfilled' ||
                                task.status === 'Error' ||
                                task.status === 'error' ||
                                task.status === 'failed')
                        );
                    }),
                    first(),
                    timeout(timeoutMs),
                    catchError((err) => {
                        console.warn('Task completion wait timed out or errored:', err);
                        return of(null);
                    })
                )
                .subscribe((task) => {
                    subscription.unsubscribe();

                    if (!task) {
                        resolve({ success: true }); // Timeout is ok
                        return;
                    }

                    const isSuccess = task.status === 'completed' || task.status === 'fulfilled';
                    resolve({
                        success: isSuccess,
                        task,
                        error: isSuccess ? undefined : task.error || 'Task failed',
                    });
                });
        });
    }

    /**
     * Show a success toast notification.
     * Use this after waiting for task completion.
     */
    public showSuccessToast(message: string, title = 'Success'): void {
        this.ngZone.run(() => this.toastr.success(message, title));
    }

    /**
     * Show an error toast notification.
     * Use this after waiting for task completion.
     */
    public showErrorToast(message: string, title = 'Error'): void {
        this.ngZone.run(() => this.toastr.error(message, title));
    }

    /**
     * Save a new part to Firestore.
     * Note: Toast notifications should be shown by the caller after waiting for task completion.
     *
     * @param part The part to save
     * @param organization Optional organization object
     * @param showToast Whether to show toast immediately (default: false - caller should wait for task completion)
     * @returns The partKey of the created part
     */
    public async saveNewPart(part: any, organization?: any, showToast = false): Promise<string> {
        const orgKey = organization?.key || this.organization.key;
        if (!orgKey) {
            console.warn('saveNewPart > No organization found.');
            throw new Error('No organization found');
        }
        const newKey = this.db
            .doc('organizations/' + orgKey)
            .collection('parts')
            .doc().ref.id;
        part.partKey = newKey; // IMPORTANT: for parts, we work with 'partKey', unlike other documents where we use 'key'.

        // Additional "blame" fields.
        const user = await this.auth.currentUser;
        part.createdBy = user?.uid || '';
        part.createdOn = new Date();
        part.modifiedBy = user?.uid || '';
        part.modifiedOn = new Date();

        await this.db
            .collection('organizations')
            .doc(orgKey)
            .collection('parts')
            .doc(part.partKey)
            .set(part)
            .then(() => {
                if (showToast) {
                    this.ngZone.run(() => this.toastr.success('Part created successfully.', 'Success'));
                }
            })
            .catch((error) => {
                console.error('saveNewPart > error:', error);
                this.ngZone.run(() => this.toastr.error('Failed to create part.', 'Error'));
                throw error;
            });

        return part.partKey;
    }

    /**
     * Update a part in Firestore.
     * Note: Toast notifications can be deferred by setting showToast to false.
     *
     * @param part The part to update
     * @param organization Optional organization object
     * @param showToast Whether to show toast immediately (default: true for backward compatibility)
     * @returns The partKey of the updated part
     */
    public async updatePart(part: OrgPart, organization?: Organization, showToast = true): Promise<string> {
        const orgKey = organization?.key || this.organization?.key;
        if (!orgKey) {
            console.warn('updatePart > No organization key found.');
            this.toastr.error('Failed to update part: Organization not identified.', 'Error');
            return Promise.reject('Organization key not found');
        }
        if (!part.partKey) {
            console.warn('updatePart > No partKey found on part.');
            this.toastr.error('Failed to update part: Part key missing.', 'Error');
            return Promise.reject('Part key missing');
        }

        const user = await this.auth.currentUser;
        const partToUpdate: Partial<OrgPart> = {
            ...part,
            modifiedBy: user?.uid || '',
            modifiedOn: Timestamp.now(), // Use Firestore Timestamp for consistency
        };

        delete (partToUpdate as any).createdOn;
        delete (partToUpdate as any).createdBy;
        // Ensure other immutable fields like id or key (if different from partKey and not meant to be updated) are handled if necessary

        return this.db
            .collection('organizations')
            .doc(orgKey)
            .collection('parts')
            .doc(part.partKey)
            .update(partToUpdate)
            .then(() => {
                if (showToast) {
                    this.ngZone.run(() => {
                        this.toastr.success('Part updated successfully.', 'Success');
                    });
                }
                return part.partKey;
            })
            .catch((error) => {
                console.error('updatePart > error:', error);
                this.ngZone.run(() => {
                    this.toastr.error('Failed to update part.', 'Error');
                });
                return Promise.reject(error);
            });
    }

    /**
     * Update multiple parts in Firestore using a batch operation.
     * Note: Toast notifications can be deferred by setting showToast to false.
     *
     * @param parts The parts to update
     * @param organization Optional organization object
     * @param showToast Whether to show toast immediately (default: true for backward compatibility)
     */
    public async updateParts(parts: OrgPart[], organization?: Organization, showToast = true): Promise<void> {
        const user = await this.auth.currentUser;
        const modifiedBy = user?.uid || '';
        const batch = this.db.firestore.batch();

        const currentOrgKey = organization?.key || this.organization?.key;
        if (!currentOrgKey) {
            console.warn('updateParts > No organization key found.');
            this.toastr.error('Failed to update parts: Organization not identified.', 'Error');
            return Promise.reject('Organization key not found');
        }

        // CRITICAL FIX: Check if any parts have path changes that require cascading updates
        const pathChangedParts = parts.filter((part) => {
            const originalPart = this.organization?.orgParts?.find((op) => op.partKey === part.partKey);
            return originalPart && originalPart.path !== part.path;
        });

        // If paths changed, we need to cascade updates to children
        let additionalPartsToUpdate: OrgPart[] = [];
        if (pathChangedParts.length > 0) {
            additionalPartsToUpdate = await this.cascadePathUpdates(
                pathChangedParts,
                this.organization?.orgParts || []
            );
        }

        // Combine original parts with cascaded updates
        const allPartsToUpdate = [...parts, ...additionalPartsToUpdate];

        const partKeys = Object.keys(
            /**
             * Use for only cleaning the data by removing fields that aren't listed.
             */
            {
                children: [],
                colour: {} as any,
                createdBy: '',
                createdOn: null as any,
                modifiedBy: '',
                modifiedOn: null as any,
                depth: 0,
                id: 0,
                key: '',
                name: '',
                parentPartKey: '',
                partKey: '',
                partType: '',
                parentRef: null as any,
                // position: 0,
                path: '',
                vfp: '',
            } as OrgPart
        ) as Array<keyof OrgPart>;

        const cleaned = allPartsToUpdate.map((part) => {
            const cleanedPart: Partial<OrgPart> = {}; // Use Partial<OrgPart>
            partKeys.forEach((key) => {
                if (part[key] !== undefined) (cleanedPart as any)[key] = part[key];
            });

            cleanedPart.modifiedBy = modifiedBy;
            cleanedPart.modifiedOn = Timestamp.now(); // Use Firestore Timestamp

            delete (cleanedPart as any).createdBy;
            delete (cleanedPart as any).createdOn;

            return cleanedPart;
        });

        cleaned.forEach((part) => {
            if (!part.partKey) {
                console.error('UpdateParts: Skipping part due to missing partKey', part);
                return;
            }
            batch.update(
                // Use update for existing parts
                this.db.collection('organizations').doc(currentOrgKey).collection('parts').doc(part.partKey).ref,
                part
            );
        });

        await batch
            .commit()
            .then(() => {
                if (showToast) {
                    this.ngZone.run(() => this.toastr.success('Parts updated successfully.', 'Success'));
                }
            })
            .catch((error) => {
                console.error('updateParts > error:', error);
                this.ngZone.run(() => this.toastr.error('Failed to update parts.', 'Error'));
                return Promise.reject(error);
            });
    }

    /**
     * Reorder children of a parent part using the atomic reorderChildren backend operation.
     *
     * This method sends a reorderChildren command document to the backend, which will:
     * 1. Validate the new child order
     * 2. Update all paths atomically (including descendants)
     * 3. Prevent race conditions that occurred with individual updatePart calls
     *
     * @param orgId - The organization ID
     * @param parentPartKey - The partKey of the parent whose children are being reordered
     * @param newChildOrder - Array of child partKeys in the new desired order
     * @returns Promise that resolves when the command document is created
     *
     * @see REORDER-CHILDREN-ARCHITECTURE.md for full design documentation
     */
    public async reorderChildren(orgId: string, parentPartKey: string, newChildOrder: string[]): Promise<void> {
        if (!orgId) {
            throw new Error('Organization ID is required');
        }

        if (!parentPartKey) {
            throw new Error('Parent part key is required');
        }

        if (!newChildOrder || newChildOrder.length === 0) {
            throw new Error('New child order must contain at least one child');
        }

        // Validate no duplicates
        const uniqueKeys = new Set(newChildOrder);
        if (uniqueKeys.size !== newChildOrder.length) {
            throw new Error('New child order contains duplicate keys');
        }

        // Get current user for blame fields
        const user = await this.auth.currentUser;
        const requestedBy = user?.uid || 'system';

        // Create a unique document ID for the command
        const commandDocId = `reorder-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

        // Create the reorderChildren command document
        // The backend trigger will detect this and create the appropriate security task
        const commandDoc = {
            partKey: commandDocId, // Temporary key for the command document
            action: 'reorderChildren',
            parentPartKey: parentPartKey,
            newChildOrder: newChildOrder,
            requestedBy: requestedBy,
            requestedAt: Timestamp.now(),
        };

        try {
            await this.db.collection('organizations').doc(orgId).collection('parts').doc(commandDocId).set(commandDoc);

            // Note: The backend will delete this command document after processing
        } catch (error) {
            console.error('❌ Failed to create reorderChildren command:', error);
            throw error;
        }
    }

    /**
     * Create multiple positions at once using the atomic bulkAddParts backend operation.
     *
     * Writes a command document that the backend processes as a single unit:
     * - Generates real partKeys for all positions
     * - Calculates all paths atomically
     * - Writes all parts in one transaction
     * - No race conditions
     *
     * @param orgId - The organization ID
     * @param anchorPartKey - The existing part to attach to
     * @param insertionType - 'child' (under anchor) or 'sibling' (next to anchor)
     * @param tree - Array of BulkPartNode roots to create
     * @returns The command document ID (used to track task completion via anchorPartKey)
     *
     * @see BULK-POSITION-BUILDER-ARCHITECTURE.md for full design documentation
     */
    public async bulkAddParts(
        orgId: string,
        anchorPartKey: string,
        insertionType: 'child' | 'sibling',
        tree: BulkPartNode[],
        siblingOrder: string[] = [],
        graftPoints: BulkGraftPoint[] = []
    ): Promise<string> {
        if (!orgId) {
            throw new Error('Organization ID is required');
        }
        if (!anchorPartKey) {
            throw new Error('Anchor part key is required');
        }
        if (!tree?.length && !siblingOrder?.length && !graftPoints?.length) {
            throw new Error('Must provide new positions, a sibling order, or graft points');
        }

        // Count total nodes in tree + graft points
        function countNodes(nodes: BulkPartNode[]): number {
            return nodes.reduce((sum, n) => sum + 1 + countNodes(n.children || []), 0);
        }
        let totalCount = countNodes(tree);
        for (const graft of graftPoints) {
            totalCount += countNodes(graft.children);
        }
        if (totalCount > 50) {
            throw new Error(`Cannot create more than 50 positions at once (got ${totalCount})`);
        }

        // Get current user for blame fields
        const user = await this.auth.currentUser;
        const requestedBy = user?.uid || 'system';

        // Create a unique document ID for the command
        const commandDocId = `bulk-add-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

        // Create the bulkAddParts command document
        const commandDoc: any = {
            partKey: commandDocId,
            action: 'bulkAddParts',
            anchorPartKey,
            insertionType,
            tree,
            totalCount,
            requestedBy,
            requestedAt: Timestamp.now(),
        };

        // Only include siblingOrder when there's meaningful interleaving
        if (siblingOrder?.length > 0) {
            commandDoc.siblingOrder = siblingOrder;
        }

        // Include graft points when present
        if (graftPoints?.length > 0) {
            commandDoc.graftPoints = graftPoints;
        }

        try {
            await this.db.collection('organizations').doc(orgId).collection('parts').doc(commandDocId).set(commandDoc);

            console.log(`✅ bulkAddParts command created: ${commandDocId} (${totalCount} positions)`);
            // Note: The backend will delete this command document after processing
        } catch (error) {
            console.error('❌ Failed to create bulkAddParts command:', error);
            throw error;
        }

        return commandDocId;
    }

    /**
     * Delete multiple positions at once using the atomic bulkDeleteParts backend operation.
     * Unlike deleteParts() which triggers individual tasks per part, this writes a single
     * command document that the backend processes atomically — finding all descendants,
     * deleting them, reindexing paths, and cleaning up hats in one transaction.
     *
     * @param partKeysToDelete - Array of part keys to delete (descendants are found automatically)
     * @param organization - Optional organization object (falls back to this.organization)
     * @returns The command document ID for monitoring
     */
    public async bulkDeleteParts(partKeysToDelete: string[], organization?: Organization): Promise<string> {
        const orgKey = organization?.key || this.organization?.key;

        if (!orgKey) {
            throw new Error('bulkDeleteParts: No organization found');
        }

        if (!partKeysToDelete.length) {
            throw new Error('bulkDeleteParts: No part keys provided');
        }

        if (partKeysToDelete.length > 50) {
            throw new Error(
                `bulkDeleteParts: Cannot delete more than 50 top-level parts at once (requested ${partKeysToDelete.length})`
            );
        }

        // Generate a unique command document ID
        const commandDocId = `cmd-bulkDelete-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
        const user = await this.auth.currentUser;

        const commandDoc = {
            action: 'bulkDeleteParts',
            partKeysToDelete: partKeysToDelete,
            totalCount: partKeysToDelete.length,
            requestedBy: user?.uid || 'unknown',
            requestedAt: new Date(),
            createdBy: user?.uid || '',
            createdOn: new Date(),
            modifiedBy: user?.uid || '',
            modifiedOn: new Date(),
        };

        try {
            await this.db.collection('organizations').doc(orgKey).collection('parts').doc(commandDocId).set(commandDoc);

            console.log(
                `✅ bulkDeleteParts command created: ${commandDocId} (${partKeysToDelete.length} top-level parts)`
            );
        } catch (error) {
            console.error('❌ Failed to create bulkDeleteParts command:', error);
            throw error;
        }

        return commandDocId;
    }

    /**
     * Atomic bulk save: bundles updates + an add-bundle + reorders + deletes into a SINGLE
     * backend task with a SINGLE sequence number. Eliminates the trigger-fire race that
     * previously occurred when the bulk-builder fanned-out N updatePart writes + 1 bulkAddParts
     * + M reorderChildren + 1 bulkDeleteParts as separate command/subcollection writes.
     *
     * Preferred entry point for the bulk position builder save flow. The backend processes
     * everything in order (updates → add → reorders → deletes) against shared in-memory
     * orgParts state and commits the final cache + all subcollection writes in one batch.
     *
     * @param orgKey   Organization id
     * @param payload  Operations to bundle. At least one of updates/add/reorders/deletes must be present.
     * @returns        The command document ID (use it to monitor task completion via diffNew.commandDocId)
     */
    public async bulkSave(
        orgKey: string,
        payload: {
            updates?: { partKey: string; fields: Record<string, any> }[];
            moves?: { partKey: string; newParentPartKey: string; newIndex?: number }[];
            add?: {
                anchorPartKey: string;
                insertionType: 'child' | 'sibling';
                tree: BulkPartNode[];
                siblingOrder?: string[];
                graftPoints?: BulkGraftPoint[];
            } | null;
            reorders?: { parentPartKey: string; newChildOrder: string[] }[];
            deletes?: string[];
        }
    ): Promise<string> {
        if (!orgKey) {
            throw new Error('bulkSave: organization key is required');
        }

        const updates = payload.updates || [];
        const moves = payload.moves || [];
        const add = payload.add || null;
        const reorders = payload.reorders || [];
        const deletes = payload.deletes || [];

        if (updates.length === 0 && moves.length === 0 && !add && reorders.length === 0 && deletes.length === 0) {
            throw new Error('bulkSave: must contain at least one operation');
        }

        // Compute add totalCount if needed
        let addPayload: any = null;
        if (add) {
            const countNodes = (nodes: BulkPartNode[]): number =>
                nodes.reduce((sum, n) => sum + 1 + countNodes(n.children || []), 0);
            let totalCount = countNodes(add.tree || []);
            for (const graft of add.graftPoints || []) {
                totalCount += countNodes(graft.children);
            }
            if (totalCount > 50) {
                throw new Error(`bulkSave: add bundle exceeds 50 positions (got ${totalCount})`);
            }
            addPayload = {
                anchorPartKey: add.anchorPartKey,
                insertionType: add.insertionType,
                tree: add.tree || [],
                totalCount,
                siblingOrder: add.siblingOrder || [],
                graftPoints: add.graftPoints || [],
            };
        }

        const user = await this.auth.currentUser;
        const requestedBy = user?.uid || 'system';

        const commandDocId = `bulk-save-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

        const commandDoc: any = {
            partKey: commandDocId,
            action: 'bulkSave',
            updates,
            moves,
            add: addPayload,
            reorders,
            deletes,
            requestedBy,
            requestedAt: Timestamp.now(),
        };

        try {
            await this.db.collection('organizations').doc(orgKey).collection('parts').doc(commandDocId).set(commandDoc);

            console.log(
                `✅ bulkSave command created: ${commandDocId} (updates=${updates.length}, moves=${moves.length}, hasAdd=${!!addPayload}, reorders=${reorders.length}, deletes=${deletes.length})`
            );
        } catch (error) {
            console.error('❌ Failed to create bulkSave command:', error);
            throw error;
        }

        return commandDocId;
    }

    /**
     * Move a single existing part (with its descendants) to a new parent at an
     * optional zero-based child index. Convenience wrapper around `bulkSave`
     * that bundles the move into a one-operation atomic command — same
     * trigger-fire-race-free serial flow that updates/adds/reorders/deletes use.
     *
     * Use this for "Move To..." actions outside an open bulk editor (e.g.
     * dragging a card across the board, picking a destination from a context
     * menu). Inside the editor or bulk builder, prefer accumulating the move
     * into the dialog's pending change-set and shipping everything together.
     *
     * @param orgKey            organization key
     * @param partKey           the part to move
     * @param newParentPartKey  destination parent partKey
     * @param newIndex          optional position in the new parent's children
     *                          (omit / undefined → append)
     * @returns the command document id (also the bulkSave task id suffix)
     */
    public async movePart(
        orgKey: string,
        partKey: string,
        newParentPartKey: string,
        newIndex?: number
    ): Promise<string> {
        return this.bulkSave(orgKey, {
            moves: [{ partKey, newParentPartKey, ...(typeof newIndex === 'number' ? { newIndex } : {}) }],
        });
    }

    /**
     * Delete parts from the organization board.
     * SIMPLIFIED VERSION: Now relies on backend to handle cascading deletions and reindexing.
     * This just deletes the specified parts and the backend task system will handle the rest.
     *
     * @param partKeys - Array of part keys to delete
     * @param organization - Optional organization object
     * @param showToast - Whether to show toast immediately (default: false - caller should wait for task completion)
     * @returns The first partKey that was deleted (for task completion monitoring)
     */
    public async deleteParts(partKeys: string[], organization?: Organization, showToast = false): Promise<string> {
        const orgKey = organization?.key || this.organization?.key;

        if (!orgKey) {
            console.warn('deletePart > No organization found.');
            throw new Error('No organization found');
        }

        if (!partKeys.length) {
            console.warn('deletePart > No part keys provided.');
            throw new Error('No part keys provided');
        }

        try {
            // Create a simple batch to delete just the specified parts
            // The backend task system will automatically:
            // 1. Detect the part deletions via Firestore triggers
            // 2. Find and delete all descendant parts
            // 3. Reindex remaining parts' paths
            // 4. Update affected hats
            // 5. Maintain data integrity

            const batch = this.db.firestore.batch();

            partKeys.forEach((partKey) => {
                const docRef = this.db.collection('organizations').doc(orgKey).collection('parts').doc(partKey);
                batch.delete(docRef.ref);
            });

            await batch.commit();

            if (showToast) {
                this.toastr.success(
                    `Successfully deleted ${partKeys.length} part(s). Cascading updates in progress...`,
                    'Success'
                );
            }

            return partKeys[0]; // Return first key for task monitoring
        } catch (error) {
            console.error('❌ Error during simplified deletion:', error);
            this.toastr.error('Failed to delete parts. Please try again.', 'Error');
            throw error;
        }
    }

    public async saveHat(hat: OrgHat): Promise<{ succeeded: boolean; entity: OrgHat }> {
        const user = await this.auth.currentUser;

        if (!hat.key) {
            hat.createdBy = user?.uid || '';
            hat.createdOn = Timestamp.now();
        }

        // Use the key if it exists, otherwise create a new one.
        hat.key = hat.key || this.db.createId();

        // Additional "blame" fields.
        hat.modifiedBy = user?.uid || '';
        hat.modifiedOn = Timestamp.now();
        // Who changed it, for the dashboard's Recent Activity (ISS-022).
        stampLastChangedBy(hat as unknown as Record<string, unknown>, user);

        return await this.db
            .collection('organizations')
            .doc(this.organization.key)
            .collection('hats')
            .doc(hat.key)
            .set(hat, { merge: true })
            .then(() => {
                this.ngZone.run(() => this.toastr.success('Saved new hat.', 'Success'));
                return {
                    succeeded: true,
                    entity: hat,
                };
            })
            .catch((error) => {
                console.error('Error saving hat: ', error);
                this.ngZone.run(() => this.toastr.error('Error saving hat', 'Error'));
                return {
                    succeeded: false,
                    entity: hat,
                };
            });
    }

    public async getDeletedHats(orgId: string): Promise<OrgHat[]> {
        const snapshot = await this.db
            .collection('organizations')
            .doc(orgId)
            .collection('hats', (ref) => ref.where('isSoftDeleted', '==', true))
            .get()
            .toPromise();
        return (snapshot?.docs || []).map((doc) => doc.data() as OrgHat);
    }

    public async saveSecurityGroup(
        securityGroup: OrgSecurityGroup
    ): Promise<{ succeeded: boolean; entity: OrgSecurityGroup }> {
        const user = await this.auth.currentUser;

        if (!securityGroup.key) {
            securityGroup.createdBy = user?.uid || '';
            securityGroup.createdOn = Timestamp.now();
        }

        // Use the key if it exists, otherwise create a new one.
        securityGroup.key = securityGroup.key || this.db.createId();

        // Additional "blame" fields.
        securityGroup.modifiedBy = user?.uid || '';
        securityGroup.modifiedOn = Timestamp.now();
        stampLastChangedBy(securityGroup as unknown as Record<string, unknown>, user);

        const docRef = this.db
            .collection('organizations')
            .doc(this.organization.key)
            .collection('securityGroups')
            .doc(securityGroup.key);

        // When using merge:true, removed featureDetails keys persist in Firestore.
        // Read the existing doc to find keys that need to be deleted after the set.
        let keysToDelete: string[] = [];
        try {
            const existingSnap = await docRef.get().toPromise();
            if (existingSnap?.exists) {
                const existingData = existingSnap.data() as any;
                const existingFD = existingData?.featureDetails || {};
                const newFD = securityGroup.featureDetails || {};
                keysToDelete = Object.keys(existingFD).filter((key) => !(key in newFD));
            }
        } catch (err) {
            console.warn('Could not read existing SG for featureDetails cleanup:', err);
        }

        return await docRef
            .set(securityGroup, { merge: true })
            .then(async () => {
                // Delete removed featureDetails keys via update() (dot-notation paths)
                if (keysToDelete.length > 0) {
                    const deletions: Record<string, any> = {};
                    for (const key of keysToDelete) {
                        deletions[`featureDetails.${key}`] = firebase.firestore.FieldValue.delete();
                    }
                    try {
                        await docRef.update(deletions);
                    } catch (err) {
                        console.warn('Failed to delete stale featureDetails keys:', err);
                    }
                }
                this.ngZone.run(() => this.toastr.success('Saved new security group.', 'Success'));
                return {
                    succeeded: true,
                    entity: securityGroup,
                };
            })
            .catch((error) => {
                console.error('Error saving security group: ', error);
                this.ngZone.run(() => this.toastr.error('Error saving security group', 'Error'));
                return {
                    succeeded: false,
                    entity: securityGroup,
                };
            });
    }

    private recursivelyUpdateChildren(nodeR: {
        nodeChildren?: unknown[];
        bc?: unknown;
        c?: unknown;
        linkColor?: unknown;
    }): void {
        if (nodeR.nodeChildren) {
            nodeR.nodeChildren.forEach((child: unknown) => {
                const typedChild = child as {
                    bc?: unknown;
                    c?: unknown;
                    linkColor?: unknown;
                    nodeChildren?: unknown[];
                };
                typedChild.bc = nodeR.bc;
                typedChild.c = nodeR.c;
                typedChild.linkColor = nodeR.linkColor;

                if (typedChild.nodeChildren && typedChild.nodeChildren.length > 0) {
                    this.recursivelyUpdateChildren(typedChild);
                }
            });
        }
    }

    public ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * CRITICAL FIX: Cascading Path Updates for Hierarchy Tree Integrity
     *
     * PROBLEM IDENTIFIED:
     * When positions are reordered/moved, their `path` values get updated, but the `parentRef`
     * values of their children are NOT updated to match the new parent path. This causes the
     * hierarchy tree to display incorrect nesting while the org board (which uses parentPartKey)
     * displays correctly.
     *
     * EXAMPLE ISSUE:
     * - Section 1 moves from index 0 to index 1: path becomes "...children.1.data"
     * - Unit 1 (child of Section 1) still has parentRef: "...children.0.data"
     * - Result: Tree can't find proper parent-child relationship
     *
     * SOLUTION:
     * This method iteratively (not recursively) updates all descendant `parentRef` values when a parent's
     * `path` changes, ensuring tree integrity is maintained.
     *
     * @param updatedParts Parts that have had their paths updated
     * @param allOrgParts Complete list of organization parts for finding children
     * @returns Array of additional parts that need updating due to cascading changes
     */
    private async cascadePathUpdates(updatedParts: OrgPart[], allOrgParts: OrgPart[]): Promise<OrgPart[]> {
        const partsToUpdate: OrgPart[] = [];
        const processedPartKeys = new Set<string>();

        // Create a map for quick lookups
        const partKeyToPartMap = new Map<string, OrgPart>();
        allOrgParts.forEach((part) => {
            if (part.partKey) {
                partKeyToPartMap.set(part.partKey, part);
            }
        });

        // Use a queue for iterative processing instead of recursion
        const partsToProcess: OrgPart[] = [...updatedParts];

        while (partsToProcess.length > 0) {
            const currentPart = partsToProcess.shift();
            if (!currentPart?.partKey) continue;

            // Find all direct children of this part
            const children = allOrgParts.filter(
                (part) =>
                    part.parentPartKey === currentPart.partKey && part.partKey && !processedPartKeys.has(part.partKey)
            );

            for (const child of children) {
                if (!child.partKey) continue; // Skip parts without partKey

                const newParentRef = currentPart.path;

                // Update the child's parentRef to match the new parent path
                const updatedChild: OrgPart = {
                    ...child,
                    parentRef: newParentRef,
                };

                partsToUpdate.push(updatedChild);
                processedPartKeys.add(child.partKey);

                // Add this child to the processing queue so its children get processed too
                partsToProcess.push(updatedChild);
            }
        }

        return partsToUpdate;
    }

    /**
     * CRITICAL FIX: Find All Descendants for Cascading Deletion
     *
     * PROBLEM:
     * The original deletion only deleted the specified parts, leaving orphaned children
     * in the database. This caused data corruption and broken relationships.
     *
     * SOLUTION:
     * This method recursively finds all descendants of the parts to be deleted,
     * ensuring complete cascading deletion that maintains data integrity.
     *
     * @param partKeys Array of partKeys to delete
     * @param organization Organization containing all parts
     * @returns Array of all partKeys that need to be deleted (including children)
     */
    private async findAllDescendantsForDeletion(partKeys: string[], organization: Organization): Promise<string[]> {
        if (!organization?.orgParts) {
            return partKeys;
        }

        const allPartsToDelete = new Set<string>(partKeys);
        const orgParts = organization.orgParts;

        // Use iterative approach to find all descendants
        let partsToProcess = [...partKeys];

        while (partsToProcess.length > 0) {
            const currentPartKeys = [...partsToProcess];
            partsToProcess = [];

            // Find direct children of current parts
            for (const partKey of currentPartKeys) {
                const children = orgParts.filter(
                    (part) => part.parentPartKey === partKey && part.partKey && !allPartsToDelete.has(part.partKey)
                );

                for (const child of children) {
                    if (child.partKey) {
                        allPartsToDelete.add(child.partKey);
                        partsToProcess.push(child.partKey);
                    }
                }
            }
        }

        const result = Array.from(allPartsToDelete);
        return result;
    }

    /**
     * ENHANCED FIX: Path Reindexing After Deletions
     *
     * PROBLEM:
     * When a position is deleted, sibling positions that come after it maintain their
     * original indices, creating gaps in the path structure (e.g., index 0 gets deleted,
     * but index 1 remains as index 1 instead of becoming index 0).
     *
     * Additionally, the previous implementation had bugs causing path corruption and
     * mismatched parentRef values.
     *
     * SOLUTION:
     * This method reindexes sibling paths after deletions and cascades the updates
     * to all descendants, ensuring data integrity is maintained.
     *
     * @param deletedPartKeys Array of partKeys that were deleted
     * @param organization Organization containing all parts
     * @returns Array of parts that need path updates due to reindexing
     */
    public async reindexPathsAfterDeletion(deletedPartKeys: string[], organization?: Organization): Promise<OrgPart[]> {
        const org = organization || this.organization;
        if (!org?.orgParts) {
            return [];
        }

        const remainingParts = org.orgParts.filter((part) => part.partKey && !deletedPartKeys.includes(part.partKey));

        const partsToUpdate: OrgPart[] = [];

        // Group remaining parts by their parent for reindexing
        const parentGroups = new Map<string, OrgPart[]>();

        remainingParts.forEach((part) => {
            const parentKey = part.parentPartKey || 'root';
            if (!parentGroups.has(parentKey)) {
                parentGroups.set(parentKey, []);
            }
            const parentGroup = parentGroups.get(parentKey);
            if (parentGroup) {
                parentGroup.push(part);
            }
        });

        // Reindex each group of siblings
        for (const [parentKey, siblings] of parentGroups) {
            if (siblings.length <= 1) continue; // No reindexing needed for single items

            // Sort siblings by their current path index to maintain relative order
            siblings.sort((a, b) => {
                const aIndex = this.extractIndexFromPath(a.path || '');
                const bIndex = this.extractIndexFromPath(b.path || '');
                return aIndex - bIndex;
            });

            // Check if any deletion gaps exist that require reindexing
            let needsReindexing = false;
            for (let i = 0; i < siblings.length; i++) {
                const expectedIndex = i;
                const actualIndex = this.extractIndexFromPath(siblings[i].path || '');
                if (actualIndex !== expectedIndex) {
                    needsReindexing = true;
                    break;
                }
            }

            if (!needsReindexing) {
                continue;
            }

            // Reassign indices starting from 0
            siblings.forEach((sibling, newIndex) => {
                const oldPath = sibling.path || '';
                const newPath = this.updatePathIndex(oldPath, newIndex);

                if (oldPath !== newPath) {
                    const updatedSibling: OrgPart = {
                        ...sibling,
                        path: newPath,
                    };

                    partsToUpdate.push(updatedSibling);

                    // CRITICAL: Also check if this sibling has children that need parentRef updates
                    // This ensures that when we change a parent's path, its children's parentRef values
                    // are updated to match the new parent path, preventing the exact validation errors
                    // we see in the analyzer output
                    const childrenOfThisSibling = remainingParts.filter(
                        (part) => part.parentPartKey === sibling.partKey
                    );

                    if (childrenOfThisSibling.length > 0) {
                        childrenOfThisSibling.forEach((child) => {
                            // CRITICAL FIX: When a parent's path changes, we need to:
                            // 1. Update the child's parentRef to match the parent's new path
                            // 2. Ensure the child's own path is consistent with the new parentRef

                            const oldParentRef = child.parentRef;
                            const newParentRef = newPath;

                            // If the child's path needs to be updated to match the new parent structure
                            let updatedChildPath = child.path;
                            if (child.path && typeof child.parentRef === 'string') {
                                // Extract the child's current index from its path
                                const childIndex = this.extractIndexFromPath(child.path);
                                // Build the new child path based on the parent's new path
                                updatedChildPath = `${newPath.replace('.data', '')}.children.${childIndex}.data`;
                            }

                            const updatedChild: OrgPart = {
                                ...child,
                                parentRef: newParentRef,
                                path: updatedChildPath,
                            };

                            // Check if this child is already in our update list
                            const existingUpdateIndex = partsToUpdate.findIndex((p) => p.partKey === child.partKey);
                            if (existingUpdateIndex >= 0) {
                                // Update the existing entry with both path and parentRef changes
                                partsToUpdate[existingUpdateIndex] = updatedChild;
                            } else {
                                // Add new entry
                                partsToUpdate.push(updatedChild);
                            }
                        });
                    }
                }
            });
        }

        // Cascade updates to children of reindexed parts
        if (partsToUpdate.length > 0) {
            const cascadedUpdates = await this.cascadePathUpdates(partsToUpdate, remainingParts);
            partsToUpdate.push(...cascadedUpdates);

            // ENHANCED: Validate the path structure after reindexing
            let validationErrors = 0;
            let fixedErrors = 0;

            // First pass: identify and try to fix validation errors
            for (let i = 0; i < partsToUpdate.length; i++) {
                const part = partsToUpdate[i];
                const validation = this.validatePathStructure(part);

                if (!validation.isValid) {
                    console.error(`❌ Path validation failed for ${part.name} (${part.partKey}): ${validation.issue}`);
                    validationErrors++;

                    // Try to auto-fix the issue by ensuring path and parentRef consistency
                    if (part.parentRef && part.path && typeof part.parentRef === 'string') {
                        // Extract the child's index from its current path
                        const childIndex = this.extractIndexFromPath(part.path);

                        // Reconstruct the path based on the parentRef
                        const parentRefBase = part.parentRef.replace('.data', '');
                        const correctedPath = `${parentRefBase}.children.${childIndex}.data`;

                        if (correctedPath !== part.path) {
                            // Update the part in the array
                            partsToUpdate[i] = {
                                ...part,
                                path: correctedPath,
                            };

                            // Re-validate
                            const revalidation = this.validatePathStructure(partsToUpdate[i]);
                            if (revalidation.isValid) {
                                fixedErrors++;
                            }
                        }
                    }
                }
            }
        }

        return partsToUpdate;
    }

    /**
     * Extract the sibling index from a path string for proper reindexing
     * We need to find what index this part has among its siblings at its depth level
     * E.g., "children.0.children.2.data" -> this part is at index 2 among its siblings under children.0
     * E.g., "children.0.children.1.children.0.data" -> this part is at index 0 among its siblings under children.0.children.1
     */
    private extractIndexFromPath(path: string): number {
        if (!path) return 0;

        // Split the path and find the last children.X pattern before .data
        const pathParts = path.split('.');
        let lastIndex = 0;

        // Work backwards from the end to find the last children.X pattern
        for (let i = pathParts.length - 1; i >= 0; i--) {
            if (pathParts[i] === 'data' && i > 0 && pathParts[i - 1].match(/^\d+$/)) {
                lastIndex = parseInt(pathParts[i - 1], 10);
                break;
            }
        }

        return lastIndex;
    }

    /**
     * Update the sibling index in a path string for proper reindexing
     * We need to update the index of this part among its siblings at its depth level
     * E.g., updatePathIndex("children.0.children.2.data", 1) -> "children.0.children.1.data"
     * E.g., updatePathIndex("children.0.children.1.children.0.data", 2) -> "children.0.children.1.children.2.data"
     */
    private updatePathIndex(path: string, newIndex: number): string {
        if (!path) return path;

        // Split the path and replace the last children.X pattern before .data
        const pathParts = path.split('.');

        // Work backwards from the end to find the last children.X pattern
        for (let i = pathParts.length - 1; i >= 0; i--) {
            if (pathParts[i] === 'data' && i > 0 && pathParts[i - 1].match(/^\d+$/)) {
                pathParts[i - 1] = newIndex.toString();
                break;
            }
        }

        return pathParts.join('.');
    }

    /**
     * Validate path structure consistency like the analyzer does
     * This helps prevent the exact validation errors we see in the analyzer output
     */
    private validatePathStructure(part: OrgPart): { isValid: boolean; issue?: string } {
        if (!part.path || !part.parentRef) {
            return { isValid: true }; // Skip validation if missing required data
        }

        // Extract parentRef path
        let parentRefPath = '';
        if (typeof part.parentRef === 'string') {
            parentRefPath = part.parentRef;
        } else if (part.parentRef && typeof part.parentRef === 'object' && 'path' in part.parentRef) {
            parentRefPath = (part.parentRef as { path: string }).path;
        }

        if (!parentRefPath) {
            return { isValid: true }; // Skip if no parentRef path
        }

        // Apply the same logic as the analyzer
        if (parentRefPath.endsWith('.data') && part.path.endsWith('.data')) {
            // Remove .data from both paths
            const parentRefBasePath = parentRefPath.replace(/\.data$/, '');
            const childPathWithoutData = part.path.replace(/\.data$/, '');

            // Remove the last "children.X" segment from child path to get the base path
            const childBasePath = childPathWithoutData.replace(/\.children\.\d+$/, '');

            // Compare the base paths - they should match
            if (childBasePath !== parentRefBasePath) {
                return {
                    isValid: false,
                    issue: `Path structure mismatch: child path base "${childBasePath}" doesn't match child's parentRef base "${parentRefBasePath}"`,
                };
            }
        }

        return { isValid: true };
    }

    /**
     * Gets the avatar URL for a user.
     * Returns photoThumbnail if available, otherwise falls back to generated avatar.
     * This is a shared method that can be used across all org-board components.
     *
     * @param user The user object (can be OrgUser, UserDetails, or any object with uid/key and name)
     * @param org Optional organization to look up user photo from orgUsers
     * @returns The avatar URL (either photoThumbnail or ui-avatars generated URL)
     */
    public getUserAvatarUrl(user: any, org?: Organization | null): string {
        // First check if user object has photoThumbnail
        if (user?.photoThumbnail) {
            return user.photoThumbnail;
        }

        // Fall back to organization's orgUsers which has the most up-to-date data
        const uid = user?.uid || user?.key;
        const orgToCheck = org || this.organization;
        if (uid && orgToCheck?.orgUsers?.[uid]?.photoThumbnail) {
            return orgToCheck.orgUsers[uid].photoThumbnail!;
        }

        // Fall back to generated avatar with profile color
        const name = user?.name || user?.display || 'User';
        return this.profileColorService.getAvatarUrl(uid || '', name);
    }
}
